import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import {
  Rocket, MapPin, QrCode, User, CheckCircle2, Circle, Clock, Users,
  StopCircle, Sparkles, Copy, Smartphone, RefreshCw, LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DemoSessionPage() {
  const nav = useNavigate();
  const token = sessionStorage.getItem("holaradar_demo_token");
  const agentName = sessionStorage.getItem("holaradar_demo_agent") || "";
  useEffect(() => { if (!token) nav("/demo/login"); }, [token, nav]);

  const [coords, setCoords] = useState(null); // {lat, lng, accuracy}
  const [locating, setLocating] = useState(false);
  const [session, setSession] = useState(null); // {session_id, area_id, area_name, qr_path, expires_at, ...}
  const [starting, setStarting] = useState(false);
  const [status, setStatus] = useState(null);  // status polling result
  const [areasStatus, setAreasStatus] = useState([]);
  const pollRef = useRef(null);
  const areasPollRef = useRef(null);

  const refreshAreas = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/demo/areas/status`);
      setAreasStatus(data.areas || []);
    } catch { /* silencio — se reintenta */ }
  }, []);

  useEffect(() => {
    refreshAreas();
    areasPollRef.current = setInterval(refreshAreas, 5000);
    return () => clearInterval(areasPollRef.current);
  }, [refreshAreas]);

  // Poll session status
  useEffect(() => {
    if (!session?.session_id) return;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/api/demo/session/${session.session_id}`);
        setStatus(data);
        if (!data.active) {
          // Sesión expiró → limpiar
          clearInterval(pollRef.current);
        }
      } catch { /* silencio — se reintenta */ }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
    return () => clearInterval(pollRef.current);
  }, [session?.session_id]);

  const takeCoords = () => {
    setLocating(true);
    if (!("geolocation" in navigator)) {
      toast.error("Tu navegador no soporta GPS");
      setLocating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setLocating(false);
        toast.success(`Coordenadas tomadas (±${Math.round(pos.coords.accuracy)}m)`);
      },
      (err) => {
        setLocating(false);
        toast.error(`No pude leer el GPS: ${err.message}`);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const startSession = async () => {
    if (!coords) return toast.error("Toma las coordenadas primero");
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/api/demo/session/start`, {
        demo_token: token,
        agent_name: agentName,
        lat: coords.lat,
        lng: coords.lng,
      });
      setSession(data);
      toast.success(`Área asignada: ${data.area_name}`);
      refreshAreas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pude reservar un área");
    } finally {
      setStarting(false);
    }
  };

  const endSession = async () => {
    if (!session?.session_id) return;
    try {
      await axios.post(`${API}/api/demo/session/end`, { session_id: session.session_id });
      toast.success("Área liberada. ¡Buena venta!");
      setSession(null);
      setStatus(null);
      refreshAreas();
    } catch {
      toast.error("No pude liberar el área");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("holaradar_demo_token");
    sessionStorage.removeItem("holaradar_demo_agent");
    nav("/demo/login");
  };

  const qrUrl = useMemo(() => {
    if (!session) return "";
    return `${window.location.origin}${session.qr_path}`;
  }, [session]);

  const copyLink = async () => {
    if (!qrUrl) return;
    try {
      await navigator.clipboard.writeText(qrUrl);
      toast.success("Link copiado");
    } catch {
      toast.info("Copia manualmente el link de abajo");
    }
  };

  const step1Done = agentName.trim().length >= 3;
  const step2Done = !!coords;
  const step3Done = !!session;
  const step4Done = !!status?.prospect_connected;

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-900 via-neutral-950 to-black text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF007A] to-[#0055FF] grid place-items-center">
              <Rocket className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">Modo Prueba</div>
              <div className="font-heading font-extrabold text-base">{agentName}</div>
            </div>
          </div>
          <Button onClick={logout} variant="ghost" className="text-neutral-400 hover:text-white h-9 rounded-full text-xs" data-testid="demo-logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Salir
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 md:p-8 grid lg:grid-cols-3 gap-6">
        {/* Columna principal: pasos */}
        <div className="lg:col-span-2 space-y-4">
          <div>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl">Guía paso a paso</h1>
            <p className="text-sm text-neutral-400 mt-1">Sigue estos 4 pasos para mostrarle HOLA RADAR al comercio.</p>
          </div>

          {/* Paso 1 */}
          <StepCard n={1} done={step1Done} title="Registra tu nombre" icon={User}>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-neutral-400">Tu nombre:</span>
              <span className="font-bold text-white">{agentName || "(sin registrar)"}</span>
            </div>
          </StepCard>

          {/* Paso 2 */}
          <StepCard n={2} done={step2Done} title="Toma tus coordenadas actuales" icon={MapPin}>
            {!coords ? (
              <div className="space-y-2">
                <p className="text-sm text-neutral-400">Le decimos a HOLA RADAR dónde estás para reubicar los comercios de prueba a tu alrededor (30-80m).</p>
                <Button
                  onClick={takeCoords}
                  disabled={locating}
                  className="rounded-full bg-[#0055FF] hover:bg-[#0044cc] font-bold h-11 px-6"
                  data-testid="demo-take-coords-btn"
                >
                  <MapPin className="w-4 h-4" /> {locating ? "Leyendo GPS…" : "Tomar coordenadas actuales"}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
                  <MapPin className="w-4 h-4 text-green-400" />
                  <span className="font-mono text-xs">{coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}</span>
                  <span className="text-neutral-500 text-xs">±{Math.round(coords.accuracy)}m</span>
                </div>
                {!session && (
                  <Button
                    onClick={startSession}
                    disabled={starting}
                    className="rounded-full bg-[#FF007A] hover:bg-[#e60070] font-bold h-11 px-6"
                    data-testid="demo-start-session-btn"
                  >
                    <Rocket className="w-4 h-4" /> {starting ? "Reservando área…" : "Iniciar prueba"}
                  </Button>
                )}
                {!session && (
                  <button onClick={takeCoords} className="text-xs text-neutral-400 hover:text-white flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> Volver a tomar
                  </button>
                )}
              </div>
            )}
          </StepCard>

          {/* Paso 3 */}
          <StepCard n={3} done={step3Done} title="Muestra el QR al gerente / dueño" icon={QrCode}>
            {!session ? (
              <p className="text-sm text-neutral-500">Primero completa los pasos anteriores para generar el QR.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4 items-start">
                <div className="bg-white rounded-2xl p-4 flex items-center justify-center" data-testid="demo-qr-box">
                  <QRCode value={qrUrl} size={200} level="M" />
                </div>
                <div className="space-y-2 text-sm">
                  <p className="text-neutral-300 leading-relaxed">
                    Dile a la persona: <em className="text-white font-bold">&ldquo;Escanea este código con la cámara de tu celular&rdquo;</em>.
                  </p>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-2 text-[10px] font-mono break-all text-neutral-400">
                    {qrUrl}
                  </div>
                  <Button onClick={copyLink} variant="outline" className="h-9 rounded-full text-xs border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="demo-copy-link">
                    <Copy className="w-3 h-3" /> Copiar link
                  </Button>
                </div>
              </div>
            )}
          </StepCard>

          {/* Paso 4 */}
          <StepCard n={4} done={step4Done} title="Aura habla con el prospecto (máx 2 min)" icon={Sparkles}>
            {!session ? (
              <p className="text-sm text-neutral-500">Aún no hay sesión activa.</p>
            ) : status?.prospect_connected ? (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
                  <Smartphone className="w-4 h-4 text-green-400" />
                  <span className="text-green-300 font-bold">Prospecto conectado</span>
                </div>
                <p className="text-neutral-300 leading-relaxed">
                  Aura ya está presentando los 5 comercios simulados en su celular. Explícale mientras: <em className="text-white">&ldquo;Fíjate cómo la app le va contando de cada tienda cerca — así funciona con tu comercio en la vida real.&rdquo;</em>
                </p>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                  <Smartphone className="w-4 h-4 text-amber-400 animate-pulse" />
                  <span className="text-amber-300 font-bold">Esperando escaneo…</span>
                </div>
                <p className="text-neutral-400">En cuanto escanee el QR arriba, esta pantalla lo detecta automáticamente.</p>
              </div>
            )}
          </StepCard>

          {session && (
            <Button
              onClick={endSession}
              variant="outline"
              className="w-full h-11 rounded-full border-red-500/40 text-red-400 hover:bg-red-500/10"
              data-testid="demo-end-session-btn"
            >
              <StopCircle className="w-4 h-4" /> Terminar sesión y liberar {session.area_name}
            </Button>
          )}
        </div>

        {/* Sidebar: timer + áreas */}
        <aside className="space-y-4">
          {/* Timer */}
          {session && status && (
            <div className={`rounded-2xl p-5 border-2 ${status.active ? "border-[#FF007A] bg-gradient-to-br from-[#FF007A]/10 to-[#0055FF]/10" : "border-neutral-700 bg-white/5"}`}>
              <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-400 flex items-center gap-1.5">
                <Clock className="w-3 h-3" /> {status.active ? "Tiempo restante" : "Sesión terminada"}
              </div>
              <div className="font-heading font-extrabold text-5xl mt-1 tabular-nums" data-testid="demo-timer">
                {formatMMSS(status.seconds_left)}
              </div>
              <div className="text-xs text-neutral-400 mt-1">
                {status.active
                  ? "Aura habla con el prospecto"
                  : "Se acabó el tiempo — reinicia la prueba abajo"}
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-neutral-300">
                <div className="w-2 h-2 rounded-full" style={{ background: session.area_color || "#FF007A" }} />
                {session.area_name} · {status.prospect_connected ? "prospecto conectado" : "esperando…"}
              </div>
            </div>
          )}

          {/* Áreas */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-400 flex items-center gap-1.5 mb-3">
              <Users className="w-3 h-3" /> Áreas de prueba
            </div>
            <div className="space-y-2">
              {areasStatus.map((a) => (
                <div key={a.area_id} className="flex items-center justify-between gap-2 py-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: a.color }} />
                    <span className="text-sm font-bold">{a.name}</span>
                    {a.area_id === session?.area_id && <span className="text-[10px] bg-[#FF007A] px-1.5 py-0.5 rounded text-white font-bold">TUYA</span>}
                  </div>
                  {a.busy ? (
                    <div className="text-right">
                      <div className="text-[10px] text-red-400 font-bold uppercase">Ocupada</div>
                      <div className="text-[10px] text-neutral-500">{a.agent_name}</div>
                    </div>
                  ) : (
                    <div className="text-[10px] text-green-400 font-bold uppercase">Libre</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Tip */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-xs text-neutral-400 leading-relaxed">
            <div className="font-bold text-white mb-1">💡 Tip para vender</div>
            Deja que Aura hable sola 30 seg. Después ábrele los ojos: <em className="text-white">&ldquo;Imagina esto con TU tienda, en vez de estos comercios de prueba. Cuando alguien pase, así conoce tus promos.&rdquo;</em>
          </div>
        </aside>
      </div>
    </div>
  );
}

function StepCard({ n, done, title, icon: Icon, children }) {
  return (
    <div className={`rounded-2xl border p-4 md:p-5 transition-all ${done ? "border-green-500/30 bg-green-500/5" : "border-white/10 bg-white/5"}`} data-testid={`demo-step-${n}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${done ? "bg-green-500 text-white" : "bg-white/10 text-white"}`}>
          {done ? <CheckCircle2 className="w-4 h-4" /> : <span className="font-heading font-extrabold text-sm">{n}</span>}
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-400">Paso {n}</div>
          <div className="font-heading font-extrabold text-base flex items-center gap-2">
            {title}
            {Icon && <Icon className="w-4 h-4 text-neutral-500" />}
          </div>
        </div>
        {done ? <CheckCircle2 className="w-5 h-5 text-green-400" /> : <Circle className="w-5 h-5 text-neutral-600" />}
      </div>
      <div className="ml-11">{children}</div>
    </div>
  );
}

function formatMMSS(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}
